import React from 'react';
import { Badge } from '../ui/badge';
import {
  Users,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import DeviceRow from './DeviceRow';

const DEVICE_TABLE_HEADERS = (
  <thead>
    <tr className="border-b bg-muted/50">
      <th className="text-left px-3 py-2 font-medium w-[140px]">Family</th>
      <th className="text-left px-3 py-2 font-medium w-[100px]">Location</th>
      <th className="text-center px-3 py-2 font-medium w-[70px]">Signed In</th>
      <th className="text-center px-3 py-2 font-medium w-[60px]">Ver</th>
      <th className="text-center px-3 py-2 font-medium w-[160px]">Target Version</th>
      <th className="text-left px-3 py-2 font-medium w-[120px]">OTA Status</th>
      <th className="text-center px-3 py-2 font-medium w-[50px]">Log</th>
      <th className="text-center px-3 py-2 font-medium w-[50px]">Set</th>
      <th className="text-center px-3 py-2 font-medium w-[50px]">Alt</th>
      <th className="text-center px-3 py-2 font-medium w-[50px]">Ovr</th>
      <th className="text-center px-3 py-2 font-medium w-[50px]">Rmt</th>
      <th className="text-center px-3 py-2 font-medium w-[50px]">TV</th>
      <th className="text-center px-3 py-2 font-medium w-[80px]">Dump</th>
      <th className="text-center px-3 py-2 font-medium w-[60px]">OS</th>
      <th className="text-center px-3 py-2 font-medium w-[160px]">Target OS</th>
      <th className="text-left px-3 py-2 font-medium w-[120px]">OS OTA Status</th>
    </tr>
  </thead>
);

function DeviceTable({ devices, sharedProps }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm table-fixed">
        {DEVICE_TABLE_HEADERS}
        <tbody>
          {devices.map(device => (
            <DeviceRow
              key={device.id}
              device={device}
              {...sharedProps}
              hasHotplug={sharedProps.hotplugFamilies.has(device.id)}
              hasOverrides={sharedProps.overrideFamilies.has(device.id)}
              alertUnreadCount={sharedProps.alertUnreadByFamily?.[device.id] || 0}
              rssi={sharedProps.deviceRssi[device.id]}
              location={sharedProps.deviceLocation?.[device.id]}
              iglooNames={sharedProps.iglooNames}
              firstSigninTime={sharedProps.firstSigninTimes[device.id]}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeviceAccountGroup({
  account,
  accountDevices,
  isExpanded,
  onToggle,
  onViewAccountDocument,
  sharedProps,
}) {
  const pingPeriodSeconds = 30;
  const pingBufferSeconds = 5;
  const onlineCount = accountDevices.filter(device => {
    const onlinePingDate = device.onlinePing?.toDate();
    return onlinePingDate && (Date.now() - onlinePingDate.getTime() < (pingPeriodSeconds + pingBufferSeconds) * 1000);
  }).length;

  return (
    <div className="border rounded-lg bg-card overflow-hidden">
      {/* Account Header */}
      <div
        className="flex items-center py-2 px-3 cursor-pointer hover:bg-muted/50 text-sm"
        onClick={() => onToggle(account.id)}
      >
        <div className="flex items-center gap-2 flex-1">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 flex-shrink-0" />
          )}
          <Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="font-medium truncate" title={account.email}>
            <span className="text-blue-600 hover:underline cursor-pointer" onClick={(e) => { e.stopPropagation(); onViewAccountDocument({ type: 'account', id: account.id }); }}>{account.email}</span> <span className="text-muted-foreground font-normal hover:text-blue-600 hover:underline cursor-pointer" onClick={(e) => { e.stopPropagation(); onViewAccountDocument({ type: 'account', id: account.id }); }}>[{account.id}]</span>
          </span>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className={onlineCount > 0 ? 'text-green-600' : ''}>
            {onlineCount}/{account.numOfFamilies || accountDevices.length} online
          </span>
          <Badge variant="outline" className="text-xs">
            {accountDevices.length} device{accountDevices.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      </div>

      {/* Expanded Content - Devices Table */}
      {isExpanded && (
        <div className="border-t">
          {accountDevices.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center">
              No devices assigned to this account
            </div>
          ) : (
            <DeviceTable devices={accountDevices} sharedProps={sharedProps} />
          )}
        </div>
      )}
    </div>
  );
}

const MemoizedDeviceAccountGroup = React.memo(DeviceAccountGroup);

// Orphaned devices section — rendered once at the bottom
function OrphanedDevicesGroup({ orphanedDevices, sharedProps }) {
  if (orphanedDevices.length === 0) return null;

  return (
    <div className="border rounded-lg bg-card overflow-hidden">
      <div className="flex items-center py-2 px-3 bg-orange-50 text-sm">
        <div className="flex items-center gap-2 flex-1">
          <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0" />
          <span className="font-medium text-orange-700">
            Orphaned Devices (not assigned to any account)
          </span>
        </div>
        <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
          {orphanedDevices.length} device{orphanedDevices.length !== 1 ? 's' : ''}
        </Badge>
      </div>
      <div className="border-t">
        <DeviceTable devices={orphanedDevices} sharedProps={sharedProps} />
      </div>
    </div>
  );
}

const MemoizedOrphanedDevicesGroup = React.memo(OrphanedDevicesGroup);

export { MemoizedDeviceAccountGroup as DeviceAccountGroup, MemoizedOrphanedDevicesGroup as OrphanedDevicesGroup };
